import { useState, useEffect, useRef } from 'react';
import {
  GoogleMap,
  StandaloneSearchBox,
  Marker,
  Circle,
  InfoWindow,
} from '@react-google-maps/api';
import { BiTargetLock } from 'react-icons/bi';
import { changeRadius } from '../store/slices/shortTermHousingRentals.slice';
import { useDispatch } from 'react-redux';
import { FaPlusCircle, FaMinusCircle } from 'react-icons/fa';
import useToggle from '../hooks/useToggle';
import Cookies from 'js-cookie';
import { nanoid } from '@reduxjs/toolkit';

const Gmap = ({ onChangeLoc, data }) => {
  const defaultCoordinates = { lat: 33.7488, lng: -84.3877 };

  // Max radius (effectively "unlimited")
  const MAX_RADIUS_MILES = 4000;

  const [center, setCenter] = useState(defaultCoordinates);
  const [searchBox, setSearchBox] = useState(null);
  const [radius, setRadius] = useState(100); // default 100 as requested
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [zoom, setZoom] = useState(7);
  const [toggleMap, handleToggleMap] = useToggle();

  // No mileage limit toggle
  const [noLimit, setNoLimit] = useState(false);
  const [lastNumericRadius, setLastNumericRadius] = useState(100);

  // Cookie
  const cookieExpiryDays = 7;

  const dispatch = useDispatch();

  const mapContainerStyle = {
    width: '100%',
    height: '300px',
  };

  const mapRef = useRef(null);
  const searchBoxRef = useRef(null);

  useEffect(() => {
    const locationCookie = Cookies.get('location');
    if (locationCookie) {
      const location = JSON.parse(locationCookie);
      setCenter({ lat: location.lat, lng: location.lng });
    } else {
      if (navigator.permissions) {
        navigator.permissions
          .query({ name: 'geolocation' })
          .then((permissionStatus) => {
            if (
              permissionStatus.state === 'granted' ||
              permissionStatus.state === 'prompt'
            ) {
              navigator.geolocation.getCurrentPosition(
                (position) => {
                  setCenter({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                  });
                },
                () => setCenter(defaultCoordinates)
              );
            } else {
              setCenter(defaultCoordinates);
            }
          })
          .catch(() => setCenter(defaultCoordinates));
      } else {
        setCenter(defaultCoordinates);
      }
    }
  }, []);

  useEffect(() => {
    // Better zoom scaling for very large radii
    const calculateZoomLevel = (radiusInMiles) => {
      const m = radiusInMiles * 1609.34;
      if (m > 3_000_000) return 3;
      if (m > 2_000_000) return 4;
      if (m > 1_000_000) return 5;
      if (m > 500_000) return 6;
      if (m > 200_000) return 7;
      if (m > 100_000) return 8;
      if (m > 50_000) return 9;
      if (m > 20_000) return 10;
      if (m > 10_000) return 11;
      return 12;
    };
    setZoom(calculateZoomLevel(radius));
  }, [radius]);

  const onLoad = (map) => {
    mapRef.current = map;
  };

  const onPlacesChanged = () => {
    if (searchBox !== null) {
      const places = searchBox.getPlaces();
      if (places.length === 0) return;

      const place = places[0];
      if (!place.geometry) return;

      const nextCenter = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };
      setCenter(nextCenter);

      const lat = nextCenter.lat;
      const lng = nextCenter.lng;
      const address = place.formatted_address || place.name || '';
      onChangeLoc(lat, lng, address);
    }
  };

  const handleSearchBoxLoad = (ref) => {
    setSearchBox(ref);
    searchBoxRef.current = ref;
  };

  const handleRadiusChange = (event) => {
    const raw = parseInt(event.target.value, 10);
    const value = isNaN(raw) ? 0 : Math.min(Math.max(raw, 0), MAX_RADIUS_MILES);
    setRadius(value);
    setLastNumericRadius(value);
    setNoLimit(value >= MAX_RADIUS_MILES); // auto-on if max
    dispatch(changeRadius(value));
  };

  const handleMarkerClick = (marker) => setSelectedMarker(marker);
  const handleInfoWindowClose = () => setSelectedMarker(null);

  const handleTargetClick = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        const currentCoords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setCenter(currentCoords);
        if (searchBoxRef.current) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: currentCoords }, (results, status) => {
            if (status === 'OK' && results[0]) {
              const address = results[0].formatted_address;
              const input = document.querySelector('.target-input');
              if (input) {
                input.value = address;
                onChangeLoc(currentCoords.lat, currentCoords.lng, address);
              }
              const bounds = new window.google.maps.LatLngBounds();
              bounds.extend(currentCoords);
              searchBoxRef.current.setBounds(bounds);
            }
          });
        }
      });
    }
  };

  const handleToggleNoLimit = () => {
    const next = !noLimit;
    setNoLimit(next);
    if (next) {
      setLastNumericRadius(radius);
      setRadius(MAX_RADIUS_MILES);
      dispatch(changeRadius(MAX_RADIUS_MILES));
    } else {
      const restore = lastNumericRadius ?? 100;
      setRadius(restore);
      dispatch(changeRadius(restore));
    }
  };

  const getRandomOffset = () => (Math.random() - 0.5) * 0.0004;

  return (
    <>
      <div className='hide-mobile'>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={center}
          zoom={zoom}
          onLoad={onLoad}
        >
          {data.map((car) => {
            let position = {};
            let title = '';
            if (car.dealer) {
              position = {
                lat: parseFloat(car.dealer.latitude) + getRandomOffset(),
                lng: parseFloat(car.dealer.longitude) + getRandomOffset(),
              };
              title = car.heading;
            } else if (car.meta) {
              position = {
                lat: parseFloat(car.meta.hp_latitude) + getRandomOffset(),
                lng: parseFloat(car.meta.hp_longitude) + getRandomOffset(),
              };
              title = car.title.rendered || car.heading;
            }

            return (
              <Marker
                key={nanoid()}
                position={position}
                onClick={() => handleMarkerClick(car)}
                title={title}
              >
                {selectedMarker === car && (
                  <InfoWindow
                    position={position}
                    onCloseClick={handleInfoWindowClose}
                  >
                    <div>
                      <h3>{title}</h3>
                      {car.meta && <p>{car.meta.hp_location}</p>}
                      <a
                        href={`${car?.link || car?.vdp_url}`}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='car-info'
                      >
                        More Info
                      </a>
                    </div>
                  </InfoWindow>
                )}
              </Marker>
            );
          })}

          <Circle
            center={center}
            radius={radius * 1609.34}
            options={{
              fillColor: '#4285F4',
              fillOpacity: 0.35,
              strokeColor: '#4285F4',
              strokeOpacity: 0.8,
              strokeWeight: 2,
              clickable: false,
              draggable: false,
              editable: false,
              visible: true,
              zIndex: 1,
            }}
          />
        </GoogleMap>
      </div>

      <div className='controls-container'>
        <div className='autocomplete-container'>
          <StandaloneSearchBox
            onLoad={handleSearchBoxLoad}
            onPlacesChanged={onPlacesChanged}
          >
            <input
              type='text'
              placeholder='Location'
              className='target-input form-control'
            />
          </StandaloneSearchBox>
          <BiTargetLock
            className='target-button'
            onClick={handleTargetClick}
            size={20}
          />
        </div>

        <div className='radius-input'>
          <div
            className='radius-wrap'
            style={{
              gap: 8,
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <span>Show results within</span>
            <input
              type='number'
              value={radius}
              className='form-control'
              min='0'
              max={MAX_RADIUS_MILES}
              step={10}
              onChange={handleRadiusChange}
              // onKeyDown={(e) => e.preventDefault()}
              disabled={noLimit}
              style={{ maxWidth: 120 }}
            />
            <span>miles</span>
          </div>

          {/* FULL-WIDTH button BELOW the controls */}
          <button
            type='button'
            onClick={handleToggleNoLimit}
            className={`btn ${noLimit ? 'btn-primary' : 'btn-outline-primary'}`}
            style={{
              display: 'block',
              width: '100%',
              marginTop: 10,
            }}
            title='Set to maximum radius'
          >
            {noLimit ? 'No mileage limit: ON' : 'No mileage limit'}
          </button>
        </div>
      </div>

      <div className='hide-desktop' style={{ marginTop: '-20px' }}>
        <button className='btn-view-map' onClick={handleToggleMap}>
          View Map{' '}
          {toggleMap ? <FaMinusCircle size={18} /> : <FaPlusCircle size={18} />}
        </button>

        {toggleMap && (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={center}
            zoom={zoom}
            onLoad={onLoad}
          >
            {data.map((car) => {
              let position = {};
              let title = '';
              if (car.dealer) {
                position = {
                  lat: parseFloat(car.dealer.latitude) + getRandomOffset(),
                  lng: parseFloat(car.dealer.longitude) + getRandomOffset(),
                };
                title = car.heading;
              } else if (car.meta) {
                position = {
                  lat: parseFloat(car.meta.hp_latitude) + getRandomOffset(),
                  lng: parseFloat(car.meta.hp_longitude) + getRandomOffset(),
                };
                title = car.title.rendered || car.heading;
              }

              return (
                <Marker
                  key={car.id}
                  position={position}
                  onClick={() => handleMarkerClick(car)}
                  title={title}
                >
                  {selectedMarker === car && (
                    <InfoWindow
                      position={position}
                      onCloseClick={handleInfoWindowClose}
                    >
                      <div>
                        <h3>{title}</h3>
                        {car.meta && <p>{car.meta.hp_location}</p>}
                        <a
                          href={`${car?.link || car?.vdp_url}`}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='car-info'
                        >
                          More Info
                        </a>
                      </div>
                    </InfoWindow>
                  )}
                </Marker>
              );
            })}

            <Circle
              center={center}
              radius={radius * 1609.34}
              options={{
                fillColor: '#4285F4',
                fillOpacity: 0.35,
                strokeColor: '#4285F4',
                strokeOpacity: 0.8,
                strokeWeight: 2,
                clickable: false,
                draggable: false,
                editable: false,
                visible: true,
                zIndex: 1,
              }}
            />
          </GoogleMap>
        )}
      </div>
    </>
  );
};

export default Gmap;
